import * as yup from 'yup'

import {
  type AuthenticationFormValues,
  AuthenticationType,
  AWSAuthenticationType,
  AWSRoleCreationMethod,
  type ObservabilityTool
} from 'components/status-checks/authentication-form-utils'
import {
  getAuthenticationUrlsFromExternalIntegration,
  getAwsDomainsFromExternalIntegrations,
  getNamesOfCustomExternalIntegrations,
  isAuthenticationUrlUnique,
  isExternalIntegrationAuthenticated,
  isObservabilityToolOfType,
  validateApiBaseUrl
} from 'components/status-checks/authentication-form-validation-utils'
import {
  ObservabilityToolType,
  type StatusCheckHeader,
  type TeamExternalIntegration,
  TeamExternalIntegrationAuthenticationStatus
} from 'types/api/status-checks/status-check'
import {
  arnRegex,
  awsAuthUrlRegex,
  awsIamArnRegex,
  getAWSAccountIdFromIamArn
} from 'utils/status-checks/status-check-integrations-util-aws'
import { isValidUrl } from 'utils/strings'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

type UniqueTestContext = yup.TestContext<StatusCheckHeader>
type UniqueSchema = yup.AnySchema<StatusCheckHeader, UniqueTestContext>

// Adds a ".unique" method to the current yup instance
yup.addMethod<yup.ArraySchema<UniqueSchema, UniqueTestContext>>(
  yup.array,
  'unique',
  function (message: string, mapper: (header: unknown) => unknown = (a) => a) {
    return this.test(
      'is-unique',
      message,
      (list = []) => list.length === new Set(list.map(mapper)).size
    )
  }
)

function getStatusCheckHeaderKey({ key }: StatusCheckHeader) {
  return key
}

/*
 * Generates AWS Observability Tool specific fields to be appended to the Authentication Form validation schema
 */
const getAwsAuthenticationFormSchema = (
  values: AuthenticationFormValues,
  externalIntegrations: TeamExternalIntegration[]
) => {
  const { awsAuthenticationType, domain, externalIntegration } = values
  const { isConfigured, integrationSpecificValues, lastAuthenticationStatus } =
    externalIntegration ?? {}
  const { AWS_IAM_ROLE_ARN: awsIamRoleArn } = integrationSpecificValues ?? {}

  // external integration flags for conditional schema composition
  const isAuthenticated =
    lastAuthenticationStatus ===
    TeamExternalIntegrationAuthenticationStatus.Authenticated
  const isAwsServiceAccount =
    awsAuthenticationType === AWSAuthenticationType.ServiceAccount

  // Used to check for duplicate AWS Account IDs when creating a new authentication
  const existingAWSIntegrationDomains =
    getAwsDomainsFromExternalIntegrations(externalIntegrations)

  return {
    /*
     * We just need to make sure the url is valid and includes the correct domain
     * it is okay for multiple AWS tools to have the same auth url
     * because the auth url is only used to run the authentication test
     * TODO: deprecate this validation check
     */
    authenticationUrl: yup
      .string()
      .trim()
      .test('is-valid-url', 'Please enter a valid URL', isValidUrl)
      .test('is-aws-authenticationUrl-valid', 'URL is not valid', (value) =>
        Boolean(value && awsAuthUrlRegex.test(value))
      )
      .required('Please enter an Authentication Endpoint URL'),
    awsAuthenticationType: yup
      .string()
      .oneOf(Object.values(AWSAuthenticationType)),
    awsRoleCreationMethod: yup
      .string()
      .oneOf(Object.values(AWSRoleCreationMethod)),
    // Conditional fields based on IAM role vs Service Account authentication type
    ...(isAwsServiceAccount
      ? {
          awsAccessKeyID: yup
            .string()
            .trim()
            .min(1, 'Please enter an AWS Access Key ID')
            .required('Please enter an AWS Access Key ID'),
          awsSecretAccessKey: yup
            .string()
            .trim()
            .min(1, 'Please enter an AWS Secret Access Key')
            .required('Please enter an AWS Secret Access Key'),
          // You cannot edit the domain (account ID) if the authentication is already configured
          ...(!isConfigured && {
            domain: yup.object().shape({
              integrationType: yup
                .string()
                .oneOf(Object.values(IntegrationType)),
              name: yup
                .string()
                .required('Please enter an AWS Account ID')
                .length(
                  12,
                  'Please enter a valid AWS Account ID (must be exactly 12 digits long)'
                )
                .notOneOf(
                  existingAWSIntegrationDomains,
                  'Account ID already in use'
                )
                .test(
                  'is-valid-account-number',
                  'Account ID cannot contain letters or special characters',
                  (value) =>
                    Boolean(
                      value && String(parseInt(value)).length === value.length
                    )
                )
            })
          })
        }
      : // ARN field validation for IAM Role authentication type
        {
          awsIamRoleArn: yup
            .string()
            .required('IAM Role ARN cannot be empty')
            .test('is-valid-arn', 'Please enter a valid ARN', (value) =>
              Boolean(value && awsIamArnRegex.test(value))
            )
            .when({
              is: (value: string) =>
                !isAuthenticated || value !== awsIamRoleArn,
              then: (schema) =>
                schema.test(
                  'is-arn-account-id-equal-to-existing-authentication-account-id',
                  'The Account ID in this ARN does not match the Account ID for this authentication. Please create a new AWS authentication to use a different Account ID.',
                  (value: string) => {
                    const parsedAccountId = getAWSAccountIdFromIamArn(value)
                    const existingAccountId = domain?.name

                    // Only check the account IDs if we are editing an existing AWS authentication
                    return existingAccountId
                      ? parsedAccountId === existingAccountId
                      : true
                  }
                )
            })
        })
  }
}

/*
 * Generates Custom Observability Tool specific fields to be appended to the Authentication Form validation schema
 */
function getCustomAuthenticationFormSchema(
  values: AuthenticationFormValues,
  externalIntegrations: TeamExternalIntegration[]
) {
  const { externalIntegration } = values
  const { lastAuthenticationStatus } = externalIntegration || {}

  const isAuthenticated =
    lastAuthenticationStatus ===
    TeamExternalIntegrationAuthenticationStatus.Authenticated
  const existingCustomIntegrationNames =
    getNamesOfCustomExternalIntegrations(externalIntegrations)
  const authenticationURLs =
    getAuthenticationUrlsFromExternalIntegration(externalIntegrations)

  return {
    /*
     * This form value serves the same function as the "apiBaseUrl" form value in the API authentication request payload
     * for the observability tool.  It replaces that value when authenticating a Custom observability tool. Since this
     * form is complex enough without a single key-value pair serving two functions, this value is stored and validated
     * separately for clarity.
     */
    authenticationUrl: yup
      .string()
      .trim()
      .test('is-valid-url', 'Please enter a valid URL', isValidUrl)
      .test(
        'endpoint-already-used',
        'URL is already in use',
        (value) =>
          isAuthenticated ||
          isAuthenticationUrlUnique(authenticationURLs, value)
      )
      .required('Please enter an Authentication Endpoint URL'),
    headers: yup
      .array()
      .of(
        yup.object({
          key: yup
            .string()
            .test(
              'is-matching-key-for-provided-value',
              'A header value is required when a header key is provided',
              (key, { parent, createError }) => {
                const keyTrimmed = key?.trim()
                const valueTrimmed: string | undefined = parent.value?.trim()

                return valueTrimmed && !keyTrimmed ? createError() : true
              }
            ),
          value: yup.string().when('key', {
            is: (value: string) => Boolean(value.trim()),
            then: (schema) =>
              schema.required(
                'A header value is required when a header key is provided'
              ),
            otherwise: (schema) => schema.nullable()
          }),
          label: yup.string().optional()
        })
      )
      .unique('Headers contain duplicate keys', getStatusCheckHeaderKey),
    ...(!isAuthenticated && {
      observabilityToolNickname: yup
        .string()
        .min(1, 'Observability Tool name cannot be empty')
        .notOneOf(existingCustomIntegrationNames, 'Name already used')
        .required('Please enter a name for this Observability Tool')
    })
  }
}

/*
 * Generates Prometheus Observability Tool specific fields to be appended to the Authentication Form validation schema
 */
const getPrometheusAuthenticationFormSchema = ({
  authentication
}: AuthenticationFormValues) => {
  return {
    authenticationUrl: yup.string().trim().required('A URL is required'),
    /*
     * Prometheus integration will contain a username/password pairing which is base encoded and
     * supplied as part of the authentication headers. The validation of these fields should follow
     * that if one field has a value, both fields are required.
     */
    authentication: yup.object().shape({
      username: yup.string().when({
        is: () => Boolean(authentication?.password),
        then: (schema) =>
          schema
            .trim()
            .required(
              'A username is required when supplying an authentication password'
            ),
        otherwise: (schema) => schema.optional()
      }),
      password: yup.string().when({
        is: () => Boolean(authentication?.username),
        then: (schema) =>
          schema
            .trim()
            .required(
              'A password is required when supplying an authentication username'
            ),
        otherwise: (schema) => schema.optional()
      })
    })
  }
}

function getIntegrationSpecificSchemaByType(
  observabilityToolType: ObservabilityToolType | undefined,
  values: AuthenticationFormValues,
  externalIntegrations: TeamExternalIntegration[]
) {
  if (!observabilityToolType) return

  switch (observabilityToolType) {
    case ObservabilityToolType.AWS:
      return getAwsAuthenticationFormSchema(values, externalIntegrations)
    case ObservabilityToolType.CUSTOM:
      return getCustomAuthenticationFormSchema(values, externalIntegrations)
    case ObservabilityToolType.PROMETHEUS:
      return getPrometheusAuthenticationFormSchema(values)
    default:
      return
  }
}

/**
 * Builds the full validation schema for the Authentication Form, including fields common to all Observability Tools
 * and fields specific to certain Observability Tool types.
 */
export function getAuthenticationFormValidationSchema(
  externalIntegrations: TeamExternalIntegration[]
) {
  return yup.lazy((values: AuthenticationFormValues) => {
    const {
      externalIntegration,
      isPrivateNetworkIntegration,
      multipleRegions,
      observabilityTool
    } = values

    const { domains, type: observabilityToolType } = observabilityTool ?? {}
    const { url } = externalIntegration ?? {}
    const expectedHeaderKeys =
      externalIntegration?.headers?.map(({ key }) => key) ?? []

    const isAWSObservabilityTool = isObservabilityToolOfType(
      ObservabilityToolType.AWS,
      observabilityTool
    )
    const isCustomObservabilityTool = isObservabilityToolOfType(
      ObservabilityToolType.CUSTOM,
      observabilityTool
    )

    const isAuthenticated =
      isExternalIntegrationAuthenticated(externalIntegration)
    const authenticationURLs =
      getAuthenticationUrlsFromExternalIntegration(externalIntegrations)

    const headersSchema = yup.object({
      key: yup
        .string()
        .min(1, 'Header key cannot be empty')
        .oneOf(expectedHeaderKeys) // Ensures the correct header key values for standard integrations
        .required('Please enter a header key'),
      value: yup
        .string()
        .min(1, 'Header value cannot be empty')
        .required('Please enter a header value'),
      label: yup.string().optional()
    })

    return yup.object().shape({
      ...(getIntegrationSpecificSchemaByType(
        observabilityToolType,
        values,
        externalIntegrations
      ) ??
        (!isAuthenticated && {
          apiBaseUrl: yup
            .string()
            .trim()
            .test('is-valid-url', 'Please enter a valid URL', isValidUrl)
            .test('is-api-base-url-valid', 'URL is not valid', (value) => {
              const { integrationType } = observabilityTool

              return Boolean(
                integrationType &&
                  validateApiBaseUrl(
                    value,
                    integrationType,
                    isPrivateNetworkIntegration ?? false
                  )
              )
            })
            .when({
              is: (value: string) => value !== url,
              then: (schema) =>
                schema.test(
                  'endpoint-already-used',
                  'URL is already in use',
                  (value) =>
                    isAuthenticationUrlUnique(authenticationURLs, value)
                )
            })
            .required('Please enter an API Base URL')
        })),
      secret: yup.string().when('authenticationType', {
        is: AuthenticationType.AWSSecretsManager,
        then: (schema) =>
          schema
            .required('ARN is required')
            .test('is-valid-arn-format', 'ARN format is invalid', (value) =>
              arnRegex.test(value)
            )
            .trim()
            .test(
              'header-values-must-be-empty',
              'Authorization Header values must be empty when ARN is provided',
              function () {
                const { headers } = this.parent

                return !headers?.some(
                  (header) => header.value && header.value.trim().length > 0
                )
              }
            ),
        otherwise: (schema) => schema.optional().nullable(true)
      }),
      customDomainName: yup
        .string()
        .nullable()
        .when({
          is: multipleRegions,
          then: (schema) =>
            schema
              .min(1, 'Domain name cannot be empty')
              .required('Please enter a domain name')
        }),
      externalIntegration: yup.mixed<TeamExternalIntegration>(),
      ...(!isAWSObservabilityTool &&
        domains?.length && {
          domain: yup.object().shape({
            name: yup.string().required('Please select a domain'),
            integrationType: yup.string().oneOf(Object.values(IntegrationType))
          })
        }),
      ...(!isAWSObservabilityTool && {
        authenticationType: yup
          .mixed<AuthenticationType | undefined>()
          .oneOf([...Object.values(AuthenticationType), undefined])
      }),
      // TODO expand logic to also support ARN for custom https://gremlininc.atlassian.net/browse/EN-9906
      ...(!isCustomObservabilityTool &&
        expectedHeaderKeys.length && {
          headers: yup
            .array()
            .when('authenticationType', {
              is: (authenticationType?: AuthenticationType) =>
                authenticationType === AuthenticationType.AuthorizationHeader ||
                !authenticationType,
              then: (schema) =>
                schema
                  .of(headersSchema)
                  .test(
                    'arn-must-be-empty',
                    'ARN must be empty when header values are provided',
                    function () {
                      const { secret } = this.parent
                      return !(secret && secret.trim().length > 0)
                    }
                  )
            })
            .unique('Header key already exists', getStatusCheckHeaderKey)
        }),
      isPrivateNetworkIntegration: yup.boolean(),
      multipleRegions: yup.boolean(),
      observabilityTool: yup
        .mixed<ObservabilityTool>()
        .required('Please select an Observability Tool')
    })
  })
}
